import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { timesheetApi } from '@/services/timesheetApi';
import { usePaginatedProjects } from '@/hooks/usePaginatedProjects';
import type { CostCentre, Project, Timesheet, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { DataMode } from '@/contexts/DataSourceContext';

export interface UseTimesheetProjectDataParams {
  screenMode: DataMode;
  projects: Project[];
  costCentres: CostCentre[];
  companyId?: string;
  entries: TimesheetEntry[];
  timesheets: Timesheet[];
  targetEmployeeId: number;
  currentWeekStart: Date;
  organizationSettings: OrganisationTimesheetSettings | undefined;
}

export interface UseTimesheetProjectDataResult {
  allAvailableProjects: Project[];
  isSearchingProjects: boolean;
  costCentresByProjectCode: Record<string, CostCentre[]>;
  handleProjectSearch: (query: string) => Promise<void>;
  fetchCostCentresForProject: (projectCode: string) => Promise<void>;
  clearFetchedCostCentresCache: () => void;
  loadInitialProjects: () => Promise<void>;
  /** Clears paginated + supplementary project state so the new company reloads cleanly. */
  resetForCompanyChange: () => void;
}

/**
 * Owns every project source the grid needs: the server-side paginated list plus
 * the supplementary lookups for projects that fall outside it (projects already
 * on the timesheet, recently-used/favourited projects, and the prior week used
 * as the Smart Auto-Fill source). Also keeps the per-project cost-centre cache
 * warm for the projects currently on the grid.
 */
export function useTimesheetProjectData({
  screenMode,
  projects,
  costCentres,
  companyId,
  entries,
  timesheets,
  targetEmployeeId,
  currentWeekStart,
  organizationSettings,
}: UseTimesheetProjectDataParams): UseTimesheetProjectDataResult {
  // Projects for existing entries that may not be in the paginated list
  const [existingEntryProjects, setExistingEntryProjects] = useState<Project[]>([]);
  // Projects from the recently-used list (localStorage) that may not be in the paginated list
  const [recentlyUsedProjects, setRecentlyUsedProjects] = useState<Project[]>([]);
  // Projects referenced by the prior week(s) used as the Smart Auto-Fill source that
  // may not be in the paginated list. Without these, generateAutoFillSuggestions
  // discards every suggestion whose project cannot be resolved.
  const [autoFillPriorWeekProjects, setAutoFillPriorWeekProjects] = useState<Project[]>([]);

  // Server-side paginated projects + per-project cost-centre fetching.
  const {
    paginatedProjects,
    isSearchingProjects,
    costCentresByProjectCode,
    loadInitialProjects,
    handleProjectSearch,
    fetchCostCentresForProject,
    resetForCompanyChange: resetPaginatedProjectsForCompanyChange,
    clearFetchedCostCentresCache,
  } = usePaginatedProjects({ screenMode, costCentres, companyId });

  // Fetch project details for existing entries that may not be in the paginated list
  const fetchExistingEntryProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;

    // Get unique project IDs from existing entries
    const existingProjectIds = new Set(
      entries
        .filter(e => e.projectId)
        .map(e => e.projectId)
    );

    // Find which project IDs are not in the paginated list
    const paginatedProjectIds = new Set(paginatedProjects.map(p => p.id));
    const missingProjectIds = Array.from(existingProjectIds).filter(
      id => id && !paginatedProjectIds.has(id)
    );

    if (missingProjectIds.length === 0) {
      setExistingEntryProjects([]);
      return;
    }

    const validIds = missingProjectIds.filter((id): id is string => !!id);
    const fetchedProjects = validIds.length > 0
      ? await timesheetApi.getProjectsByIds(validIds)
      : [];
    setExistingEntryProjects(fetchedProjects);
  }, [entries, paginatedProjects, screenMode]);

  // Fetch recently used and favourited projects that may not be in the
  // paginated list.  The useProjectSelector hook stores these IDs in
  // localStorage but relies on the projects array it receives to build the
  // "Recently Used" and "Favourites" sections.  In live mode only the first
  // page of projects is loaded, so any saved project outside that page
  // silently disappears from the dropdown.  This mirrors the
  // fetchExistingEntryProjects pattern.
  const fetchRecentlyUsedProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;

    let savedIds: string[] = [];
    const storageKey = companyId
      ? `project-selector-state-${companyId}`
      : 'project-selector-state';
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const recentIds: string[] = (parsed.recentlyUsed || []).map((item: { id: string }) => item.id);
        const favouriteIds: string[] = parsed.favourites || [];
        const unique = new Set([...recentIds, ...favouriteIds]);
        savedIds = Array.from(unique);
      }
    } catch {
      return;
    }

    if (savedIds.length === 0) {
      setRecentlyUsedProjects([]);
      return;
    }

    const knownIds = new Set(paginatedProjects.map(p => p.id));
    const missingIds = savedIds.filter(id => id && !knownIds.has(id));

    if (missingIds.length === 0) {
      setRecentlyUsedProjects([]);
      return;
    }

    const fetched = missingIds.length > 0
      ? await timesheetApi.getProjectsByIds(missingIds)
      : [];
    setRecentlyUsedProjects(fetched);
  }, [paginatedProjects, screenMode, companyId]);

  // Fetch project details referenced by the prior week that Smart Auto-Fill uses
  // as its source. generateAutoFillSuggestions discards any suggestion whose
  // projectId cannot be resolved in the projects it is given, so a prior-week
  // project outside the loaded/paginated set would silently produce the empty
  // "No suggestions available" state. This mirrors the fetchExistingEntryProjects
  // / fetchRecentlyUsedProjects pattern and resolves those projects regardless
  // of pagination.
  const fetchAutoFillPriorWeekProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;

    // Mirror generateAutoFillSuggestions: consider only the target employee's
    // weeks earlier than the current week, most recent first, and pick the first
    // week that has reusable manual (timesheet-native) entries.
    const currentWeekStartTime = currentWeekStart.getTime();
    const priorWeeks = timesheets
      .filter(ts =>
        ts.employeeId === targetEmployeeId &&
        new Date(ts.weekEnding).getTime() < currentWeekStartTime
      )
      .sort((a, b) => new Date(b.weekEnding).getTime() - new Date(a.weekEnding).getTime());

    let sourceProjectIds: string[] = [];
    for (const priorWeek of priorWeeks) {
      const nativeProjectIds = (priorWeek.entries || [])
        .filter(entry =>
          !entry.isFromSchedule &&
          !entry.sourceIntegration &&
          !entry.absenceType &&
          !!entry.projectId
        )
        .map(entry => entry.projectId as string);

      if (nativeProjectIds.length > 0) {
        sourceProjectIds = Array.from(new Set(nativeProjectIds));
        break;
      }
    }

    if (sourceProjectIds.length === 0) {
      setAutoFillPriorWeekProjects([]);
      return;
    }

    const knownIds = new Set(paginatedProjects.map(p => p.id));
    const missingIds = sourceProjectIds.filter(id => !knownIds.has(id));

    if (missingIds.length === 0) {
      setAutoFillPriorWeekProjects([]);
      return;
    }

    const fetched = await timesheetApi.getProjectsByIds(missingIds);
    setAutoFillPriorWeekProjects(fetched);
  }, [timesheets, targetEmployeeId, currentWeekStart, paginatedProjects, screenMode]);

  // Load initial projects on mount
  useEffect(() => {
    loadInitialProjects();
  }, [loadInitialProjects]);

  // Fetch existing entry projects when entries or paginated projects change
  useEffect(() => {
    fetchExistingEntryProjects();
  }, [fetchExistingEntryProjects]);

  // Fetch recently used projects when paginated projects change
  useEffect(() => {
    fetchRecentlyUsedProjects();
  }, [fetchRecentlyUsedProjects]);

  // Fetch prior-week projects used by Smart Auto-Fill when timesheets/week change
  useEffect(() => {
    fetchAutoFillPriorWeekProjects();
  }, [fetchAutoFillPriorWeekProjects]);

  // Combine paginated projects with existing entry projects for the selector
  const allAvailableProjects = useMemo(() => {
    let result: Project[];
    if (screenMode === 'demo') {
      result = projects;
    } else {
      const seen = new Set<string>();
      const combined: Project[] = [];
      for (const p of [...paginatedProjects, ...existingEntryProjects, ...recentlyUsedProjects, ...autoFillPriorWeekProjects]) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          combined.push(p);
        }
      }
      result = combined;
    }

    const scope = organizationSettings?.projectSelectionScope ?? 'all';

    if (scope === 'all') {
      result = result.filter(p => {
        const status = p.status?.toLowerCase();
        return status === 'active' || status === 'planning';
      });
    } else if (scope === 'assigned') {
      const usedProjectIds = new Set(entries.map(e => e.projectId).filter(Boolean));
      result = result.filter(p => p.isFavourite || usedProjectIds.has(p.id));
    } else {
      const showArchived = organizationSettings?.showArchivedProjects ?? false;
      if (!showArchived) {
        result = result.filter(p => p.active !== false || (p.status && p.status.toLowerCase() === 'active'));
      }
    }

    return result;
  }, [screenMode, projects, paginatedProjects, existingEntryProjects, recentlyUsedProjects, autoFillPriorWeekProjects, organizationSettings?.showArchivedProjects, organizationSettings?.projectSelectionScope, entries]);

  // Fetch cost centres for existing entries when entries change
  // This ensures cost centres are available for entries that already have a project selected
  useEffect(() => {
    const projectCodes = new Set<string>();
    entries.forEach(entry => {
      if (entry.projectId) {
        // Use allAvailableProjects to find the project (includes paginated + existing entry projects)
        const project = allAvailableProjects.find(p => p.id === entry.projectId);
        if (project?.code) {
          projectCodes.add(project.code);
        }
      }
    });
    // Fetch cost centres for all unique project codes
    projectCodes.forEach(code => fetchCostCentresForProject(code));
  }, [entries, allAvailableProjects, fetchCostCentresForProject]);

  const resetForCompanyChange = useCallback(() => {
    resetPaginatedProjectsForCompanyChange();
    setExistingEntryProjects([]);
    setRecentlyUsedProjects([]);
    setAutoFillPriorWeekProjects([]);
  }, [resetPaginatedProjectsForCompanyChange]);

  return {
    allAvailableProjects,
    isSearchingProjects,
    costCentresByProjectCode,
    handleProjectSearch,
    fetchCostCentresForProject,
    clearFetchedCostCentresCache,
    loadInitialProjects,
    resetForCompanyChange,
  };
}
